import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { normalizeDecimal, DECIMAL_HINT, convertQty, recipeFactor } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Factory, ChefHat, AlertTriangle, CheckCircle2, Package, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Produktion · Planung
 * Neuen Produktionsauftrag anlegen mit automatischem Rohstoffbedarf +
 * Lagerbestand-Check (Ampel-Anzeige).
 */
export default function ProductionPlanning() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [bundles, setBundles] = useState({});
  const [items, setItems] = useState({}); // id → item (for finished-good dropdown)
  const defaultMhd = () => {
    const d = new Date();
    d.setDate(d.getDate() + 450);
    return d.toISOString().slice(0, 10);
  };
  const [form, setForm] = useState({
    finished_item_id: "",
    recipe_id: "",
    planned_quantity: "",
    planned_date: new Date().toISOString().slice(0, 10),
    output_charge_number: "",
    output_mhd: defaultMhd(),
    bundle_code: "",
    notes: "",
  });
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/recipes").then((r) => setRecipes(r.data)).catch(() => {});
    api.get("/settings/bundles").then((r) => setBundles(r.data)).catch(() => {});
    api.get("/items").then((r) => {
      const idx = {};
      (r.data || []).forEach((it) => { idx[it.id] = it; });
      setItems(idx);
    }).catch(() => {});
  }, []);

  // Fertigware auswählen → Rezeptur automatisch laden (die erste, deren output_item_id passt)
  useEffect(() => {
    if (!form.finished_item_id) return;
    const rec = recipes.find((r) => r.output_item_id === form.finished_item_id);
    if (rec) setForm((f) => ({ ...f, recipe_id: rec.id }));
  }, [form.finished_item_id, recipes]);

  // Volle Rezeptur laden (mit Zutaten inkl. Stock) sobald recipe_id gesetzt
  useEffect(() => {
    if (!form.recipe_id) { setSelectedRecipe(null); return; }
    let cancelled = false;
    api.get(`/recipes/${form.recipe_id}`)
      .then((r) => { if (!cancelled) setSelectedRecipe(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.recipe_id]);

  // Charge-Nummer Vorschlag: Backend-Auto-Generator nutzen, damit
  // die Warengruppen-Nr. (z. B. „601" aus erp.popcrn.de) korrekt an
  // das geplante Datum angehängt wird. Fällt auf reines Datum + Random
  // zurück, wenn der Endpoint nicht antwortet (Runde 126.1).
  useEffect(() => {
    if (form.output_charge_number || !form.planned_date) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/batches/next-charge-number", {
          params: {
            item_id: form.finished_item_id || undefined,
            today: form.planned_date,
          },
        });
        if (!cancelled && data?.charge_number) {
          setForm((f) => (f.output_charge_number ? f : { ...f, output_charge_number: data.charge_number }));
        }
      } catch {
        if (!cancelled) {
          const seq = String(Math.floor(Math.random() * 900) + 100);
          setForm((f) => (f.output_charge_number ? f : { ...f, output_charge_number: `${f.planned_date}-${seq}` }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [form.planned_date, form.finished_item_id, form.output_charge_number]);

  const finishedGoodOptions = useMemo(
    () => Object.values(items).filter((it) => it.type === "finished" || it.type === "finished_good"),
    [items]
  );

  // Trüffel-Sonderregel: nur freitags produzierbar (Hinweis + Auto-Korrektur)
  const isTruffle = useMemo(() => {
    const n = `${items[form.finished_item_id]?.name || ""} ${selectedRecipe?.name || ""}`;
    return /tr(ü|u)ffel|truffle/i.test(n);
  }, [form.finished_item_id, items, selectedRecipe]);
  const truffleDateMismatch = useMemo(() => {
    if (!isTruffle || !form.planned_date) return false;
    return new Date(`${form.planned_date}T00:00:00`).getDay() !== 5; // 5 = Freitag
  }, [isTruffle, form.planned_date]);
  const nextFriday = (iso) => {
    const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
    d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7));
    return d.toISOString().slice(0, 10);
  };

  const preview = useMemo(() => {
    const rec = selectedRecipe && selectedRecipe.id === form.recipe_id
      ? selectedRecipe
      : recipes.find((r) => r.id === form.recipe_id);
    const qty = Number(form.planned_quantity);
    if (!rec || !(qty > 0)) return null;

    const outQty = Number(rec.output_quantity || 1) || 1;
    const outUnit = (rec.output_unit || "stk").toLowerCase();
    const outName = rec.output_item_name || "Fertigware";
    const bundleCfg = form.bundle_code && bundles[form.bundle_code];
    const bundleUnits = bundleCfg?.units || 0;
    // Runde 125.4: Unit-Match zwischen `qty` (Stück) und Recipe-Output.
    // Wenn das Rezept in g/ml definiert ist, rechnen wir zuerst
    // Stück → Gramm um (item.weight_g), damit der Faktor stimmt.
    const outItem = items[rec.output_item_id];
    const weightG = Number(outItem?.weight_g || 0);
    // Geplante Menge = Einheiten der Fertigware, gleiche Regel wie das Backend.
    const ingredientFactor = recipeFactor(qty, outQty, outUnit, weightG);
    const equivQty = ingredientFactor * outQty;
    // Runde 126.11 – Header muss die tatsächlich zu produzierende
    // Rohstoff-Basis zeigen, nicht `qty × outQty` (das wäre die
    // Rezept-Ausbringung × geplante Menge und ignoriert die Fertigware-
    // Größe `weight_g`). Beispiel:
    //   Rezept „5kg Sack" outQty=5000 g, Fertigware „…80 g" weight_g=80.
    //   geplant 13 Beutel → 13 × 80 = 1040 g Popcorn → Faktor 1040/5000 = 0,208.
    //   Alte Header „13 × 5000 g = 65 kg" war irreführend und wirkte
    //   als würden 65 kg produziert.
    const previewTotal = equivQty;      // gesamte Rohstoff-Basis
    const previewUnit  = (weightG > 0 && (outUnit === "g" || outUnit === "ml"))
      ? outUnit                          // wir haben in g/ml umgerechnet
      : outUnit;

    const ingredients = [];
    const bagItemId = rec.bag_item_id || null;
    let bagRendered = false;
    for (const ing of (selectedRecipe?.ingredients || [])) {
      // Runde 126.6 – Tüte / Grundverpackung ist immer 1:1 mit der geplanten
      // Menge (1 Beutel je produzierte Einheit), unabhängig vom
      // Rezept-Faktor. Wenn der Bag-Artikel als reguläre Rezept-Zutat
      // hinterlegt ist, überschreiben wir Menge und Einheit; Faktor 12,5
      // × Menge sind Legacy-Werte, die 13 913 statt 1 113 erzeugen.
      const isBag = bagItemId && ing.item_id === bagItemId;
      const needed = isBag
        ? qty
        : Number(ing.quantity || 0) * ingredientFactor;
      const unit = isBag ? "stk" : ing.unit;
      const rawStock = Number(ing.stock ?? 0);
      const stock = convertQty(rawStock, ing.item_unit || unit, unit);
      const short = Math.max(needed - stock, 0);
      ingredients.push({ ...ing, unit, needed, stock, short, is_bag: isBag });
      if (isBag) bagRendered = true;
    }
    // Wenn die Rezeptur `bag_item_id` gesetzt hat, die Zutat aber NICHT
    // in `ingredients` steht, virtuelle Zeile injizieren, damit der
    // Bedarf sofort sichtbar ist.
    if (bagItemId && !bagRendered) {
      const bagItem = items[bagItemId] || {};
      ingredients.push({
        item_id: bagItemId,
        item_name: bagItem.name || "Tüte",
        item_sku: bagItem.sku || "",
        unit: "stk",
        item_unit: bagItem.unit || "stk",
        needed: qty,
        stock: Number(bagItem.stock ?? 0),
        short: Math.max(qty - Number(bagItem.stock ?? 0), 0),
        is_bag: true,
      });
    }
    const anyShort = ingredients.some((i) => i.short > 0);

    return { rec, qty, outQty, outUnit, outName, ingredients, anyShort,
             bundleCfg, bundleUnits, previewTotal, previewUnit, weightG,
             ingredientFactor };
  }, [form.recipe_id, form.planned_quantity, form.bundle_code, selectedRecipe, recipes, bundles]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recipe_id) { toast.error("Rezeptur wählen"); return; }
    if (!form.planned_quantity) { toast.error("Menge angeben"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/production", {
        recipe_id: form.recipe_id,
        planned_quantity: Number(form.planned_quantity),
        planned_date: form.planned_date || null,
        output_charge_number: form.output_charge_number || null,
        output_mhd: form.output_mhd || null,
        bundle_code: form.bundle_code || null,
        notes: form.notes || null,
      });
      if (data?.schedule_notice) toast.warning(data.schedule_notice);
      else toast.success(`Produktionsauftrag angelegt · Charge ${form.output_charge_number || "auto"}`);
      // Formular teilweise leeren, Datum/Bundle beibehalten für serielle Anlage
      setForm((f) => ({ ...f, finished_item_id: "", recipe_id: "",
                        planned_quantity: "",
                        output_charge_number: "",
                        output_mhd: defaultMhd(), notes: "" }));
      setSelectedRecipe(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-production-planning">
      <PageHeader
        eyebrow="Produktion"
        title="Planung"
        description="Neuen Produktionsauftrag anlegen. Rohstoffbedarf und Lagerbestand werden automatisch berechnet — inkl. Ampel-Warnung bei Fehlmenge."
        actions={
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/produktion/monitor")}
                  data-testid="btn-goto-monitor">
            <Factory size={16} className="mr-1" /> Zum Monitor
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="bg-white border border-[var(--border)] rounded-2xl p-5 space-y-4">
          <div>
            <Label>Fertigware *</Label>
            <Select value={form.finished_item_id}
                    onValueChange={(v) => setForm({ ...form, finished_item_id: v })}>
              <SelectTrigger data-testid="select-finished-good"><SelectValue placeholder="Fertigware wählen…" /></SelectTrigger>
              <SelectContent>
                {finishedGoodOptions.map((it) => (
                  <SelectItem key={it.id} value={it.id}>
                    {it.name} <span className="text-[var(--text-muted)]">({it.sku})</span>
                  </SelectItem>
                ))}
                {finishedGoodOptions.length === 0 && <SelectItem value="__empty" disabled>Keine Fertigware-Artikel angelegt</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Rezeptur * <span className="text-xs text-[var(--text-muted)] font-normal">(wird automatisch geladen)</span></Label>
            <Select value={form.recipe_id} onValueChange={(v) => setForm({ ...form, recipe_id: v })}>
              <SelectTrigger data-testid="select-recipe">
                <SelectValue placeholder="Rezeptur…" />
                <ChefHat size={14} className="ml-2 opacity-60" />
              </SelectTrigger>
              <SelectContent>
                {recipes.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Geplante Menge *</Label>
              <Input required type="number" step="any" placeholder={DECIMAL_HINT}
                     data-testid="input-planned-qty"
                     value={form.planned_quantity}
                     onChange={(e) => setForm({ ...form, planned_quantity: normalizeDecimal(e.target.value) })} />
            </div>
            <div>
              <Label>Geplantes Datum</Label>
              <Input type="date" data-testid="input-planned-date"
                     value={form.planned_date}
                     onChange={(e) => setForm({ ...form, planned_date: e.target.value })} />
              {truffleDateMismatch && (
                <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5"
                     data-testid="truffle-friday-hint">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>
                    Trüffel wird nur freitags produziert.{" "}
                    <button type="button" className="underline font-semibold"
                            data-testid="truffle-fix-date"
                            onClick={() => setForm((f) => ({ ...f, planned_date: nextFriday(f.planned_date) }))}>
                      Auf nächsten Freitag setzen
                    </button>
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Charge-Bezeichnung *</Label>
              <Input required data-testid="input-charge-number"
                     placeholder="2026-09-16-601"
                     value={form.output_charge_number}
                     onChange={(e) => setForm({ ...form, output_charge_number: e.target.value })} />
              <div className="text-[10px] text-[var(--text-muted)] mt-0.5">Vorschlag: <span className="font-mono">Datum-Warengruppe</span> (Warengruppen-Nr. aus dem Artikel). Wird beim Ändern von Artikel oder Datum neu vorgeschlagen — überschreibbar.</div>
            </div>
            <div>
              <Label>Ziel-MHD</Label>
              <Input type="date" data-testid="input-mhd"
                     value={form.output_mhd}
                     onChange={(e) => setForm({ ...form, output_mhd: e.target.value })} />
              <div className="text-[10px] text-[var(--text-muted)] mt-0.5">Standardvorschlag: heute + 450 Tage — überschreibbar.</div>
            </div>
          </div>

          <div>
            <Label>Bundle-Größe (optional)</Label>
            <Select value={form.bundle_code || "none"}
                    onValueChange={(v) => setForm({ ...form, bundle_code: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="select-bundle"><SelectValue placeholder="Kein Bundle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Einzelprodukt (kein Bundle)</SelectItem>
                {Object.entries(bundles)
                  // Nur Bundles anzeigen, die entweder für alle Rezepturen
                  // gelten (leere product_item_ids) oder das aktuelle
                  // Fertigprodukt enthalten. Runde 126.9.
                  .filter(([, cfg]) => {
                    const targets = cfg.product_item_ids || [];
                    if (targets.length === 0) return true;
                    return targets.includes(form.finished_item_id);
                  })
                  .map(([code, cfg]) => (
                  <SelectItem key={code} value={code} data-testid={`bundle-opt-${code}`}>
                    {cfg.label || code}{cfg.type ? ` · ${cfg.type}` : ""} · {cfg.units} Einheiten
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notiz</Label>
            <Input data-testid="input-notes" value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          <Button type="submit" disabled={busy || !form.recipe_id || !form.planned_quantity}
                  data-testid="btn-create-production"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold w-full">
            {busy ? "Speichere…" : (<><Factory size={16} className="mr-1" /> Produktionsauftrag anlegen</>)}
          </Button>
        </form>

        {/* Preview panel */}
        <div className="bg-white border border-[var(--border)] rounded-2xl p-5" data-testid="preview-panel">
          <div className="overline text-xs mb-3 flex items-center gap-2">
            <Package size={14} /> Rohstoffbedarf & Lagerbestand
          </div>

          {!preview && (
            <div className="text-center py-10 text-[var(--text-muted)] text-sm">
              Wähle Fertigware und Menge — der Bedarf erscheint automatisch.
            </div>
          )}

          {preview && (
            <div className="space-y-4">
              <div className="bg-[var(--pop-cream)]/40 rounded-xl p-3 text-sm">
                <div className="font-semibold">
                  <span className="font-mono">{preview.qty}</span>
                  {preview.weightG > 0
                    ? <> Packung(en) à <span className="font-mono">{preview.weightG}</span> g</>
                    : <> × <span className="font-mono">{preview.outQty}{preview.outUnit}</span></>}
                  <ArrowRight size={12} className="inline mx-2 opacity-60" />
                  <span className="font-mono text-[var(--pop-pink)]">
                    {(() => {
                      const v = preview.previewTotal;
                      const u = preview.previewUnit;
                      if ((u === "g" || u === "ml") && v >= 1000) {
                        return `${(v / 1000).toLocaleString("de-DE", { maximumFractionDigits: 3 })} ${u === "g" ? "kg" : "l"}`;
                      }
                      return `${v.toLocaleString("de-DE", { maximumFractionDigits: 3 })} ${u}`;
                    })()}
                  </span> {preview.outName}
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  Rezept-Ausbringung {preview.outQty}{preview.outUnit} · Rohstoff-Faktor ×{preview.ingredientFactor.toLocaleString("de-DE", { maximumFractionDigits: 4 })}
                </div>
                {preview.bundleUnits > 0 && (
                  <div className="text-xs text-[var(--text-muted)] mt-1">
                    Bundle {preview.bundleCfg?.label}: {preview.bundleUnits} Einheiten pro Bundle
                  </div>
                )}
              </div>

              {preview.anyShort ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800 flex items-start gap-2"
                     data-testid="warning-shortage">
                  <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
                  <div><b>Achtung Fehlmenge:</b> Bestand einiger Zutaten reicht NICHT für den geplanten Auftrag.</div>
                </div>
              ) : preview.ingredients.length > 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800 flex items-start gap-2"
                     data-testid="ok-stock">
                  <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" />
                  <div><b>Bestand ausreichend:</b> Alle Rohstoffe können bereitgestellt werden.</div>
                </div>
              ) : null}

              <div className="space-y-1">
                {preview.ingredients.map((ing, i) => {
                  const isShort = ing.short > 0;
                  return (
                    <div key={i}
                         className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center text-xs py-1.5 border-b border-[var(--border)]/60 last:border-b-0"
                         data-testid={`preview-ing-${i}`}>
                      <span className="truncate font-medium">
                        {ing.is_bag && <span className="pop-pill bg-pink-100 text-pink-800 text-[9px] font-bold mr-1 uppercase" title="Grundverpackung – 1 Beutel je produzierte Einheit">Tüte</span>}
                        {ing.item_name || ing.item_sku || "?"}
                      </span>
                      <span className="font-mono">
                        {ing.needed.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {ing.unit}
                      </span>
                      <span className="font-mono text-[var(--text-muted)]">
                        / {ing.stock.toLocaleString("de-DE", { maximumFractionDigits: 3 })} {ing.unit}
                      </span>
                      {isShort ? (
                        <span className="pop-pill bg-red-100 text-red-700 text-[10px] font-bold whitespace-nowrap"
                              data-testid={`ing-short-${i}`}>
                          −{ing.short.toLocaleString("de-DE", { maximumFractionDigits: 3 })}
                        </span>
                      ) : (
                        <span className="pop-pill bg-green-100 text-green-700 text-[10px] font-bold">OK</span>
                      )}
                    </div>
                  );
                })}
                {preview.ingredients.length === 0 && (
                  <div className="text-xs text-[var(--text-muted)] py-4 text-center">
                    Rezeptur hat keine Zutaten definiert.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
